import React, { useEffect, useState } from "react";
import Router from "./router";
import "./assets/space/css/main.css";
import "./App.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { SpaceProvider } from "context/spaceContext";
import { useSessionRestore } from "./hooks/useSessionRestore";

const GlobalEntitlementListener = React.lazy(() =>
  import("./components/billing").then((m) => ({ default: m.GlobalEntitlementListener }))
);

/** Mount billing prompts after idle so marketing first paint is not blocked by MUI in entitlement chunk. */
function DeferredGlobalEntitlementListener() {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const schedule = () => setReady(true);
    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(schedule, { timeout: 4000 });
      return () => window.cancelIdleCallback(id);
    }
    if (typeof window !== "undefined") {
      const t = window.setTimeout(schedule, 800);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, []);
  if (!ready) return null;
  return (
    <React.Suspense fallback={null}>
      <GlobalEntitlementListener />
    </React.Suspense>
  );
}

function App() {
  /**
   * Phase 1 session bootstrap:
   * - On initial app load, validate any existing token with the backend
   * - Hydrate Redux ONLY from the backend response
   * - This hook does not read Redux; it treats the backend as the source of truth
   */
  const { sessionRestoreFailed, retryRestore } = useSessionRestore();

  return (
    <SpaceProvider>
      <div className="App">
        <DeferredGlobalEntitlementListener />
        <ToastContainer
          position="top-right"
          autoClose={4000}
          hideProgressBar
          newestOnTop
          closeOnClick
          pauseOnHover
          draggable={false}
          toastStyle={{
            borderRadius: '10px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
            padding: '14px 18px',
            fontSize: '14px',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontWeight: 500,
            lineHeight: 1.5,
            color: '#1a202c',
            background: '#fff',
            border: '1px solid #e5e7eb',
          }}
        />
        {sessionRestoreFailed ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100vh",
              gap: 16,
              padding: 24,
            }}
          >
            <p style={{ margin: 0, color: "var(--text-secondary, #666)" }}>
              Session restore failed. Check your connection and try again.
            </p>
            <button
              type="button"
              className="session-restore-retry-btn"
              onClick={retryRestore}
            >
              Retry
            </button>
          </div>
        ) : (
          <Router />
        )}
      </div>
    </SpaceProvider>
  );
}

export default App;
